import { DownOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Grid, Input, Modal, Typography } from "antd";
import type { InputRef } from "antd";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export type SearchNavNode = {
  key: string;
  label: string;
  icon?: ReactNode;
  children?: SearchNavNode[];
};

type VisibleNode = SearchNavNode & { depth: number; isBranch: boolean };

export function GlobalSearch({ searchItems, isDark, accentColor }: {
  searchItems: SearchNavNode[];
  isDark: boolean;
  accentColor: string;
}) {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusIndex, setFocusIndex] = useState(0);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const inputRef = useRef<InputRef | null>(null);

  const term = query.trim().toLowerCase();
  const filteredTree = useMemo(() => {
    if (!term) return searchItems;
    const filter = (nodes: SearchNavNode[]): SearchNavNode[] => nodes.flatMap((node) => {
      const children = filter(node.children ?? []);
      const matches = `${node.label} ${node.key}`.toLowerCase().includes(term);
      if (matches) return [node];
      return children.length ? [{ ...node, children }] : [];
    });
    return filter(searchItems);
  }, [searchItems, term]);

  const visibleNodes = useMemo<VisibleNode[]>(() => {
    const flatten = (nodes: SearchNavNode[], depth = 0): VisibleNode[] => nodes.flatMap((node) => {
      const isBranch = Boolean(node.children?.length);
      const row = { ...node, depth, isBranch };
      const showChildren = isBranch && (Boolean(term) || expandedKeys.has(node.key));
      return [row, ...(showChildren ? flatten(node.children!, depth + 1) : [])];
    });
    return flatten(filteredTree);
  }, [expandedKeys, filteredTree, term]);

  const pageCount = useMemo(() => {
    const count = (nodes: SearchNavNode[]): number => nodes.reduce((total, node) => total + (node.children?.length ? count(node.children) : 1), 0);
    return count(filteredTree);
  }, [filteredTree]);

  const openSearch = useCallback(() => {
    setOpen(true);
    setQuery("");
    setFocusIndex(0);
    setExpandedKeys(new Set());
    window.setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  const closeSearch = useCallback(() => { setOpen(false); setQuery(""); setFocusIndex(0); }, []);
  const selectItem = useCallback((item: SearchNavNode) => { navigate(item.key); closeSearch(); }, [closeSearch, navigate]);
  const toggleBranch = (key: string) => setExpandedKeys((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [openSearch]);

  useEffect(() => { setFocusIndex(0); }, [query]);
  useEffect(() => { setFocusIndex((index) => Math.min(index, Math.max(visibleNodes.length - 1, 0))); }, [visibleNodes.length]);
  useEffect(() => {
    if (!open) return;
    document.querySelector(".global-search-tree-row.is-focused")?.scrollIntoView({ block: "nearest" });
  }, [focusIndex, open]);

  const bg = isDark ? "#1e293b" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.12)" : "#e2e8f0";
  const text = isDark ? "#f1f5f9" : "#1e293b";
  const secondary = isDark ? "#94a3b8" : "#64748b";
  const hover = isDark ? "rgba(255,255,255,0.08)" : "#f8fafc";

  return <>
    <Button className="global-search-trigger" type="text" icon={<SearchOutlined />} aria-label="Search pages" onClick={openSearch}>
      {screens.md ? "Search pages" : null}
      {screens.lg ? <span className="global-search-shortcut">Ctrl+K</span> : null}
    </Button>
    <Modal
      rootClassName="global-search-modal"
      open={open}
      onCancel={closeSearch}
      footer={null}
      closable={false}
      width={620}
      style={{ top: screens.md ? "10vh" : 0 }}
      styles={{ content: { padding: 0, borderRadius: screens.md ? 10 : 0, background: bg, overflow: "hidden" }, mask: { backdropFilter: "blur(4px)" } }}
      destroyOnClose
    >
      <div onKeyDownCapture={(event) => {
        const focusedNode = visibleNodes[focusIndex];
        if (event.key === "ArrowDown") {
          event.preventDefault(); event.stopPropagation();
          setFocusIndex((index) => Math.min(index + 1, visibleNodes.length - 1));
        } else if (event.key === "ArrowUp") {
          event.preventDefault(); event.stopPropagation();
          setFocusIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === "ArrowRight" && focusedNode?.isBranch && !term && !expandedKeys.has(focusedNode.key)) {
          event.preventDefault(); event.stopPropagation(); toggleBranch(focusedNode.key);
        } else if (event.key === "ArrowLeft" && focusedNode?.isBranch && !term && expandedKeys.has(focusedNode.key)) {
          event.preventDefault(); event.stopPropagation(); toggleBranch(focusedNode.key);
        } else if (event.key === "Enter" && focusedNode) {
          event.preventDefault(); event.stopPropagation();
          if (focusedNode.isBranch) { if (!term) toggleBranch(focusedNode.key); }
          else selectItem(focusedNode);
        } else if (event.key === "Escape") {
          event.preventDefault(); event.stopPropagation(); closeSearch();
        }
      }}>
      <div className="global-search-input-row" style={{ borderColor: border }}>
        <SearchOutlined style={{ color: accentColor, fontSize: 18 }} />
        <Input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search accessible pages..."
          variant="borderless"
          style={{ color: text, background: "transparent", fontSize: 16 }}
        />
      </div>
      <div className="global-search-tree-heading" style={{ color: secondary, borderColor: border }}>
        <span>{term ? "Search results" : "Navigation"}</span>
        <small>{pageCount} {pageCount === 1 ? "page" : "pages"}</small>
      </div>
      <div className="global-search-results" role="tree" aria-label="Page navigation">
        {!visibleNodes.length ? <Typography.Text style={{ display: "block", padding: 32, textAlign: "center", color: secondary }}>No accessible pages found</Typography.Text> :
          visibleNodes.map((item) => {
            if (item.isBranch) {
              const expanded = Boolean(term) || expandedKeys.has(item.key);
              const index = visibleNodes.findIndex((node) => node.key === item.key);
              const focused = index === focusIndex;
              return <button
                type="button"
                key={item.key}
                role="treeitem"
                aria-expanded={expanded}
                className={`global-search-tree-row global-search-branch${focused ? " is-focused" : ""}`}
                onMouseEnter={() => setFocusIndex(index)}
                onClick={() => !term && toggleBranch(item.key)}
                style={{ paddingLeft: 16 + item.depth * 22, color: focused ? accentColor : text, background: focused ? hover : "transparent", borderLeftColor: focused ? accentColor : "transparent" }}
              >
                <span className="global-search-caret" style={{ color: secondary }}>{expanded ? <DownOutlined /> : <RightOutlined />}</span>
                <span className="global-search-result-icon" style={{ color: accentColor }}>{item.icon}</span>
                <span>{item.label}</span>
                <small style={{ color: secondary }}>{item.children?.length}</small>
              </button>;
            }
            const index = visibleNodes.findIndex((node) => node.key === item.key);
            const focused = index === focusIndex;
            return <button
              type="button"
              key={item.key}
              role="treeitem"
              className={`global-search-tree-row global-search-result${focused ? " is-focused" : ""}`}
              onMouseEnter={() => setFocusIndex(index)}
              onClick={() => selectItem(item)}
              style={{ paddingLeft: 17 + item.depth * 22, background: focused ? hover : "transparent", borderLeftColor: focused ? accentColor : "transparent", color: focused ? accentColor : text }}
            >
              <span className="global-search-result-icon" style={{ color: focused ? accentColor : secondary }}>{item.icon}</span>
              <span>{item.label}</span>
              <small style={{ color: secondary }}>{item.key}</small>
            </button>;
          })}
      </div>
      <div className="global-search-footer" style={{ color: secondary, borderColor: border }}>
        <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span><span><kbd>Enter</kbd> Open</span><span><kbd>Esc</kbd> Close</span>
      </div>
      </div>
    </Modal>
  </>;
}
